import { useState, useCallback } from "react";
import {
  getTeacherScoreFeedback,
  getStudentScoreFeedback,
  getTeacherEventsOverviewFeedback,
  getStudentEventsOverviewFeedback,
  getTeacherCourseFeedback,
  getStudentCourseFeedback,
  getTeacherDotClickFeedback,
  getStudentDotClickFeedback,
} from "../api/getScoreFeedback";

/**
 * Custom hook for managing tab switch feedback API calls
 * @param {object} params - Parameters for the hook
 * @returns {object} Hook state and functions
 */
export const useTabSwitchFeedback = ({
  role,
  teacher_id,
  studentID,
  courses,
  selectedcoursebystudent,
  selectedStudentDatas,
  OverallAvgScoreStudentside,
  periodTab,
  year,
  month,
  startdate,
  enddate,
  month_year,
  currentYear,
  token,
}) => {
  const [isTabSwitching, setIsTabSwitching] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState(null);

  const handleTabSwitch = useCallback(
    async (newValue) => {
      try {
        setIsTabSwitching(true);
        
        // Don't automatically fetch existing feedback on tab switch
        // Always show "Generate with AI" button instead
        setExistingFeedback(null);
      } catch (error) {
        console.error("Error handling tab switch:", error);
        setExistingFeedback(null);
      } finally {
        setIsTabSwitching(false);
      }
    },
    []
  );

  const resetTabSwitchState = useCallback(() => {
    setIsTabSwitching(false);
    setExistingFeedback(null);
  }, []);

  const clearExistingFeedback = useCallback(() => {
    setExistingFeedback(null);
  }, []);

  // Function to fetch course-specific feedback
  const fetchCourseFeedback = useCallback(
    async (courseId) => {
      try {
        const context = {
          teacher_id: role === "Teacher" ? teacher_id : undefined,
          student_id: role === "Student" ? studentID : undefined,
          selectedCourseData: [courseId],
          selectedStudentData:
            role === "Teacher" ? selectedStudentDatas : [studentID],
          avg_score: OverallAvgScoreStudentside,
          periodTab: periodTab,
          year: year || currentYear,
          month: month,
          startdate: startdate,
          enddate: enddate,
          month_year: month_year,
          week_start_date: startdate, // Add week_start_date for month time periods
        };

        let response;
        if (role === "Teacher") {
          response = await getTeacherCourseFeedback(context, token);
        } else {
          response = await getStudentCourseFeedback(context, token);
        }

        if (
          response?.feedback_data?.results &&
          response.feedback_data.results.length > 0
        ) {
          const latestFeedback = response.feedback_data.results.reduce(
            (latest, current) => {
              const currentDate = new Date(current.created_at);
              const latestDate = latest
                ? new Date(latest.created_at)
                : new Date(0);
              return currentDate > latestDate ? current : latest;
            }
          );
          return latestFeedback;
        }
        return null;
      } catch (error) {
        console.error("Error fetching course feedback:", error);
        return null;
      }
    },
    [
      role,
      teacher_id,
      studentID,
      selectedStudentDatas,
      OverallAvgScoreStudentside,
      periodTab,
      year,
      month,
      startdate,
      enddate,
      month_year,
      currentYear,
      token,
    ]
  );

  // Function to fetch dot click feedback
  const fetchDotClickFeedback = useCallback(
    async (dotData) => {
      try {
        const context = {
          teacher_id: role === "Teacher" ? teacher_id : undefined,
          student_id: role === "Student" ? studentID : undefined,
          selectedCourseData: [parseInt(dotData?.course_id)],
          selectedStudentData:
            role === "Teacher"
              ? [parseInt(dotData?.student_id, 10)]
              : [studentID],
          avg_score: dotData?.score || 0,
          periodTab: periodTab,
          year: year || currentYear,
          month: month,
          startdate: startdate,
          enddate: enddate,
          month_year: month_year,
          daily_date: dotData?.originalDate || dotData?.submission_date,
          week_start_date: startdate, // Add week_start_date for month time periods
        };

        let response;
        if (role === "Teacher") {
          response = await getTeacherDotClickFeedback(context, token);
        } else {
          response = await getStudentDotClickFeedback(context, token);
        }

        if (
          response?.feedback_data?.results &&
          response.feedback_data.results.length > 0
        ) {
          const latestFeedback = response.feedback_data.results.reduce(
            (latest, current) => {
              const currentDate = new Date(current.created_at);
              const latestDate = latest
                ? new Date(latest.created_at)
                : new Date(0);
              return currentDate > latestDate ? current : latest;
            }
          );
          return latestFeedback;
        }
        return null;
      } catch (error) {
        console.error("Error fetching dot click feedback:", error);
        return null;
      }
    },
    [
      role,
      teacher_id,
      studentID,
      periodTab,
      year,
      month,
      startdate,
      enddate,
      month_year,
      currentYear,
      token,
    ]
  );

  return {
    isTabSwitching,
    handleTabSwitch,
    resetTabSwitchState,
    existingFeedback,
    clearExistingFeedback,
    fetchCourseFeedback,
    fetchDotClickFeedback,
  };
};
